import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress, Chip, Divider, Typography, OutlinedInput, Snackbar, Alert, Stepper, Step, StepLabel, Box, Card, CardContent, Grid, Paper, ToggleButtonGroup, ToggleButton, InputAdornment, Tooltip, Switch, FormControlLabel } from '@mui/material';
import { useTeamsTeamMembersCreateMutation, useServicesSalonListQuery } from '../../../../services/GLOWClient';
import { useSalonTeamsQuery } from '../../../../services/teamsApi';
// import { useTeamsTeamsSalonTeamsRetrieveQuery } from '../../../../services/GLOWClient'; // TODO: Endpoint not generated
import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import MuiSelect from '../../../../SystemDesign/MuiSelect';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CoffeeIcon from '@mui/icons-material/Coffee';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../../../context/AuthContext';
import AddSchedule from '../schedule/addSchedule';
import { getLocalizedField } from '../../../../utils/localizedFields';


const AddMember = ({ open, onClose, salonId = null, selectedSalonId = null, preselectedTeam = null, onSuccess }) => {
    const { t, i18n } = useTranslation();
    
    // Use salonId prop (from new structure) or fallback to selectedSalonId (legacy)
    const currentSalonId = salonId || selectedSalonId;
    
    // Stepper state
    const [activeStep, setActiveStep] = useState(0);
    const [createdMember, setCreatedMember] = useState(null);
    
    const steps = [t('pro_team_member.add_member.step_create_member'), t('pro_team_member.add_member.step_create_planning')];
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });
    
    const showError = (message) => {
        setSnackbar({
            open: true,
            message: message,
            severity: 'error'
        });
    };
    
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        has_access: true,
        team_ids: [], // Changed from team to team_ids for multi-select
        service_ids: [],
        salon_id: currentSalonId || null
    });
    
    // Schedule dialog state
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    
    // Update salon ID when props change
    useEffect(() => {
        if (currentSalonId && formData.salon_id !== currentSalonId) {
            setFormData(prev => ({
                ...prev,
                salon_id: currentSalonId
            }));
        }
    }, [currentSalonId, formData.salon_id]);
    
    // Handle preselected team
    useEffect(() => {
        if (preselectedTeam && open) {
            const teamId = preselectedTeam.team_id || preselectedTeam.id;
            setFormData(prev => ({
                ...prev,
                team_ids: [teamId]
            }));
        }
    }, [preselectedTeam, open]);
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const { user } = useAuth();
    
    // Fetch services data for the selected salon
    const activeSalonId = formData.salon_id || currentSalonId;
    const {
        data: servicesData,
        isLoading: servicesLoading,
        isFetching: servicesFetching,
        refetch: refetchServices
    } = useServicesSalonListQuery(
        activeSalonId ? { salonId: activeSalonId, language: i18n.language } : undefined,
        {
            skip: !activeSalonId,
            refetchOnMountOrArgChange: true,
        }
    );

    useEffect(() => {
        if (open && activeSalonId) {
            refetchServices();
        }
    }, [open, activeSalonId, refetchServices]);
    
    // Fetch teams for the specific salon using the dedicated endpoint
    const {
        data: salonTeamsData,
        isLoading: teamsLoading,
        error: teamsError
    } = useSalonTeamsQuery(activeSalonId, {
        skip: !activeSalonId || !open,
        refetchOnMountOrArgChange: true,
    });
    
    // Use the salon teams data directly - no client-side filtering needed
    const filteredTeams = React.useMemo(() => {
        if (!salonTeamsData) return [];
        return Array.isArray(salonTeamsData) ? salonTeamsData : [];
    }, [salonTeamsData]);
    
 
    // Use the team members create mutation to create new employees
    const [createTeamMember, { isLoading: isCreating }] = useTeamsTeamMembersCreateMutation();
    
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };
    
    // Handle service selection
    const handleServiceChange = (event) => {
        const {
            target: { value },
        } = event;
        
        // Convert to numbers since service_ids are numeric
        const numericValues = value.map(v => typeof v === 'string' ? parseInt(v, 10) : v);
        
        setFormData({
            ...formData,
            service_ids: numericValues
        });
        
        // Clear error when field is edited
        if (errors.service_ids) {
            setErrors({
                ...errors,
                service_ids: ''
            });
        }
    };
    
    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[+]?[0-9]{1,15}$/;
        
        // Team is not required in addmember.jsx
        // Removed the validation for team field
        // Salon validation is not needed since it's provided via props
        
        if (formData.has_access) {
            if (!formData.email) {
                newErrors.email = t('pro_team_member.add_member.validation.email_required');
            } else if (!emailRegex.test(formData.email)) {
                newErrors.email = t('pro_team_member.add_member.validation.email_invalid');
            }
        }
        
        if (!formData.first_name) {
            newErrors.first_name = t('pro_team_member.add_member.validation.first_name_required');
        }
        
        if (!formData.last_name) {
            newErrors.last_name = t('pro_team_member.add_member.validation.last_name_required');
        }
        
        // Validate phone number if provided
        if (formData.phone && !phoneRegex.test(formData.phone)) {
            newErrors.phone = t('pro_team_member.add_member.validation.phone_invalid');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        if (activeStep === 0) {
            // Step 1: Create member
            if (!validateForm()) {
                return;
            }
            
            try {
                // Create the team member (employee) with services if selected
                const teamMemberData = {
                    teamMemberRequest: {
                        email: formData.has_access ? formData.email : undefined,
                        first_name: formData.first_name,
                        last_name: formData.last_name,
                        phone: formData.has_access ? (formData.phone || null) : null,
                        has_access: !!formData.has_access,
                        service_ids: formData.service_ids.length > 0 ? formData.service_ids : undefined,
                        team_ids: formData.team_ids.length > 0 ? formData.team_ids : undefined,
                        salon: activeSalonId || undefined // Add salon field
                    }
                };
                
                const result = await createTeamMember(teamMemberData).unwrap();
                
                // Store created member and proceed to schedule creation
                setCreatedMember(result);
                
                // Don't call onSuccess immediately - wait for complete flow
                // The onSuccess will be called after schedule creation or if user skips it
                
                // Move to next step
                setActiveStep(1);
                setShowScheduleDialog(true);
                
            } catch (error) {
                console.error('Error creating team member:', error);
                handleMemberCreationError(error);
            }
        }
    };
    
    const handleMemberCreationError = (error) => {
        // Handle API validation errors
        if (error.data) {
            // Handle different error formats with French translations
            let errorMessage = '';
            const apiErrors = {};

            // Check for duplicate email error (most common case)
            if (error.data.details && error.data.details.includes('duplicate key value violates unique constraint "users_user_email_key"')) {
                errorMessage = t('pro_team_member.add_member.errors.email_already_used');
                apiErrors.email = t('pro_team_member.add_member.errors.email_already_used_short');
            }
            // Format 1: Standard error with details
            else if (error.data.details) {
                errorMessage = error.data.details;
                // Clean up technical details for better user experience
                if (errorMessage.includes('duplicate key value violates unique constraint')) {
                    errorMessage = t('pro_team_member.add_member.errors.value_exists');
                }
            }
            // Format 2: Error object with error property
            else if (error.data.error && typeof error.data.error === 'string') {
                errorMessage = error.data.error === 'Internal server error' ? t('pro_team_member.add_member.errors.server_error') : error.data.error;
            }
            // Format 3: Field-specific errors (most important - these are validation errors)
            else if (error.data.errors && typeof error.data.errors === 'object') {
                const fieldTranslations = {
                    'email': t('pro_team_member.add_member.errors.field_email'),
                    'first_name': t('pro_team_member.add_member.errors.field_first_name'),
                    'last_name': t('pro_team_member.add_member.errors.field_last_name'),
                    'phone': t('pro_team_member.add_member.errors.field_phone'),
                    'service_ids': t('pro_team_member.add_member.errors.field_services'),
                    'team_ids': t('pro_team_member.add_member.errors.field_teams')
                };

                const errorMessages = [];
                Object.entries(error.data.errors).forEach(([key, value]) => {
                    const fieldName = fieldTranslations[key] || key;
                    const formattedValue = Array.isArray(value) ? value.join(', ') : value;
                    apiErrors[key] = formattedValue;
                    errorMessages.push(`${fieldName}: ${formattedValue}`);
                });
                
                errorMessage = errorMessages.join(' | ');
            }
            // Format 3b: DRF field-specific errors directly on the payload
            // Example: {"phone": ["Ce numéro de téléphone existe déjà"]}
            else if (typeof error.data === 'object') {
                const fieldTranslations = {
                    'email': t('pro_team_member.add_member.errors.field_email'),
                    'first_name': t('pro_team_member.add_member.errors.field_first_name'),
                    'last_name': t('pro_team_member.add_member.errors.field_last_name'),
                    'phone': t('pro_team_member.add_member.errors.field_phone'),
                    'service_ids': t('pro_team_member.add_member.errors.field_services'),
                    'team_ids': t('pro_team_member.add_member.errors.field_teams')
                };

                const entries = Object.entries(error.data)
                    .filter(([key]) => !['detail', 'details', 'error', 'message'].includes(key));

                if (entries.length > 0) {
                    const errorMessages = [];
                    entries.forEach(([key, value]) => {
                        const fieldName = fieldTranslations[key] || key;
                        const formattedValue = Array.isArray(value) ? value.join(', ') : value;
                        apiErrors[key] = formattedValue;
                        errorMessages.push(`${fieldName}: ${formattedValue}`);
                    });

                    // Prefer a short toast (first error), keep full list for debugging/form
                    errorMessage = errorMessages[0] || '';
                }
            }
            // Format 4: Generic detail field
            else if (error.data.detail && typeof error.data.detail === 'string') {
                errorMessage = error.data.detail;
            }
            // Format 5: Check for non_field_errors
            else if (error.data.non_field_errors) {
                const errors = Array.isArray(error.data.non_field_errors) 
                    ? error.data.non_field_errors 
                    : [error.data.non_field_errors];
                errorMessage = errors.join(' | ');
            }

            // If still no error message, provide a generic one
            if (!errorMessage) {
                errorMessage = t('pro_team_member.add_member.errors.create_error');
            }

            // Set form errors if we have field-specific errors
            if (Object.keys(apiErrors).length > 0) {
                setErrors(apiErrors);
            } else {
                setErrors({ submit: errorMessage });
            }
            
            // Always show the most specific error message in the snackbar
            showError(errorMessage);
        } else {
            const errorMessage = error.message || t('pro_team_member.add_member.errors.unexpected_error');
            setErrors({ submit: errorMessage });
            showError(errorMessage);
        }
    };
    
    // Handle schedule creation success
    const handleScheduleSuccess = (createdSchedules) => {
        // Call onSuccess callback with both member and schedule info
        if (onSuccess && createdMember) {
            onSuccess(t('pro_team_member.member_created_with_planning'), createdMember);
        }
        
        // Reset forms and close dialog
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
            has_access: true,
            service_ids: [],
            team_ids: [],
            salon_id: currentSalonId || null
        });
        setActiveStep(0);
        setCreatedMember(null);
        setShowScheduleDialog(false);
        onClose();
    };
    
    // Handle schedule dialog close (when user skips schedule creation)
    const handleScheduleClose = () => {
        // Call onSuccess with just member info since schedule was skipped
        if (onSuccess && createdMember) {
            onSuccess(t('pro_team_member.member_created'), createdMember);
        }
        
        setShowScheduleDialog(false);
        // Reset forms and close main dialog
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
            has_access: true,
            service_ids: [],
            team_ids: [],
            salon_id: currentSalonId || null
        });
        setActiveStep(0);
        setCreatedMember(null);
        onClose();
    };
    
    // Reset form when dialog is closed
    useEffect(() => {
        if (!open && !dialogClosed) {
            setFormData({
                email: '',
                first_name: '',
                last_name: '',
                phone: '',
                has_access: true,
                team_ids: [], // Changed from team to team_ids
                salon_id: currentSalonId || null
            });
            setErrors({});
            setActiveStep(0);
            setCreatedMember(null);
        }
        
        if (open) {
            setDialogClosed(false);
        }
    }, [open, dialogClosed]);
    
    // No specialities needed for user creation
    
    const dialogContent = (
        <div>
            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {errors.submit && (
                <div style={{ color: theme.colors.error.main, marginBottom: '16px' }}>
                    {errors.submit}
                </div>
            )}
            
            {activeStep === 0 ? (
                // Step 1: Member Creation Form
                <div>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, mb: { xs: 1, md: 2 } }}>
                        {t('pro_team_member.add_member.employee_info_title')}
                    </Typography>

            <FormControlLabel
                control={
                    <Switch
                        checked={!formData.has_access}
                        onChange={(e) => {
                            const sansAcces = e.target.checked;
                            setFormData(prev => ({
                                ...prev,
                                has_access: !sansAcces,
                                email: sansAcces ? '' : prev.email,
                                phone: sansAcces ? '' : prev.phone,
                            }));
                            setErrors(prev => {
                                const next = { ...prev };
                                delete next.email;
                                delete next.phone;
                                return next;
                            });
                        }}
                        disabled={isCreating}
                    />
                }
                label={formData.has_access ? t('pro_team_member.add_member.with_access') : t('pro_team_member.add_member.without_access')}
                sx={{ mb: 1 }}
            />

            {formData.has_access && (
            
            <TextField
                fullWidth
                label={t('pro_team_member.add_member.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                    // Always update the form data first
                    const { name, value } = e.target;
                    setFormData(prev => ({
                        ...prev,
                        [name]: value
                    }));
                    
                    // Then validate email
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (value && !emailRegex.test(value)) {
                        setErrors(prev => ({
                            ...prev,
                            email: t('pro_team_member.add_member.validation.email_invalid_format')
                        }));
                    } else if (errors.email) {
                        // Clear error if valid
                        setErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.email;
                            return newErrors;
                        });
                    }
                }}
                margin="dense"
                error={!!errors.email}
                helperText={errors.email}
                disabled={isCreating}
                inputProps={{
                    inputMode: 'email'
                }}
                sx={{ '& .MuiInputBase-root': { fontSize: { xs: '0.875rem', md: '1rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            )}
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1, md: 2 } }}>
                <TextField
                    fullWidth
                    label={t('pro_team_member.add_member.first_name')}
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    margin="dense"
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    disabled={isCreating}
                    sx={{ '& .MuiInputBase-root': { fontSize: { xs: '0.875rem', md: '1rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
                />
                
                <TextField
                    fullWidth
                    label={t('pro_team_member.add_member.last_name')}
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    margin="dense"
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                    disabled={isCreating}
                    sx={{ '& .MuiInputBase-root': { fontSize: { xs: '0.875rem', md: '1rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
                />
            </Box>
            
            {formData.has_access && (
            <TextField
                fullWidth
                label={t('pro_team_member.add_member.phone_required')}
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                    // Always update the form data first
                    const { name, value } = e.target;
                    
                    // Only allow valid phone characters
                    if (value && !/^[+]?[0-9]*$/.test(value)) {
                        // Invalid character - don't update form data
                        setErrors(prev => ({
                            ...prev,
                            phone: t('pro_team_member.add_member.validation.phone_invalid')
                        }));
                        return;
                    }
                    
                    // Update form data with valid input
                    setFormData(prev => ({
                        ...prev,
                        [name]: value
                    }));
                    
                    // Clear error if it exists
                    if (errors.phone) {
                        setErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.phone;
                            return newErrors;
                        });
                    }
                }}
                margin="dense"
                error={!!errors.phone}
                helperText={errors.phone}
                disabled={isCreating}
                inputProps={{
                    pattern: '[+]?[0-9]{1,15}',
                    inputMode: 'tel'
                }}
                sx={{ '& .MuiInputBase-root': { fontSize: { xs: '0.875rem', md: '1rem' } }, '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
            />
            )}
            
            
            
            
            {/* Teams multi-select dropdown */}
            <div style={{ marginTop: '8px', marginBottom: '4px' }}>
                <MuiSelect
                    label={t('pro_team_member.add_member.teams_optional')}
                    list={filteredTeams?.map(team => ({ id: team.team_id, name: team.name })) || []}
                    selectedValue={formData.team_ids || []}
                    onChange={(e) => {
                        setFormData({
                            ...formData,
                            team_ids: e.target.value
                        });
                        
                        // Clear error when field is edited
                        if (errors.team_ids) {
                            setErrors({
                                ...errors,
                                team_ids: ''
                            });
                        }
                    }}
                    disabled={isCreating || teamsLoading || !activeSalonId}
                    multiple={true}
                    noneText={!activeSalonId ? t('pro_team_member.add_member.select_salon_first') : 
                              teamsLoading ? t('pro_team_member.add_member.loading_teams') :
                              teamsError ? t('pro_team_member.add_member.loading_teams_error') :
                              t('pro_team_member.add_member.no_teams_available')}
                />
                {errors.team_ids && <FormHelperText error>{errors.team_ids}</FormHelperText>}
            </div>
            
            {/* Services multi-select dropdown */}
            <div style={{ marginTop: '16px', marginBottom: '8px' }}>
                <MuiSelect
                    label={t('pro_team_member.add_member.services')}
                    list={servicesData?.map(service => ({ id: service.service_id, name: getLocalizedField(service, 'name', i18n.language) })) || []}
                    selectedValue={formData.service_ids || []}
                    onChange={handleServiceChange}
                    disabled={isCreating || servicesLoading || servicesFetching || !activeSalonId}
                    multiple={true}
                    noneText={(servicesLoading || servicesFetching) ? t('pro_team_member.add_member.loading_services') : t('pro_team_member.add_member.no_services_available')}
                />
                {errors.service_ids && <FormHelperText error>{errors.service_ids}</FormHelperText>}
            </div>
                </div>
            ) : (
                // Step 2: Show success message and prepare for schedule creation
                <div>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {t('pro_team_member.add_member.member_created_success')}
                    </Typography>
                    
                    {/* Created Member Success Card */}
                    {createdMember && (
                        <Card variant="outlined" sx={{ mb: 3, backgroundColor: 'rgba(76, 175, 80, 0.08)', border: '2px solid rgba(76, 175, 80, 0.3)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'success.main', mb: 2 }}>
                                    <PersonIcon sx={{ mr: 1.5, fontSize: 28 }} />
                                    {t('pro_team_member.add_member.new_team_member_added')}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                            <strong>{t('pro_team_member.add_member.full_name')}:</strong> {createdMember.user_details?.full_name || `${createdMember.user_details?.first_name} ${createdMember.user_details?.last_name}`}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            <strong>{t('pro_team_member.add_member.email')}:</strong> {createdMember.user_details?.email}
                                        </Typography>
                                        {createdMember.user_details?.phone && (
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>{t('pro_team_member.add_member.phone')}:</strong> {createdMember.user_details.phone}
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            <strong>{t('pro_team_member.add_member.member_id')}:</strong> {createdMember.team_member_id || createdMember.id}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>{t('pro_team_member.add_member.salon')}:</strong> {createdMember.salon_name || t('pro_team_member.add_member.selected_salon')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'success.dark' }}>
                                    {t('pro_team_member.add_member.member_created_now_planning')}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                    
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        {t('pro_team_member.add_member.click_create_planning')}
                    </Typography>
                </div>
            )}
        </div>
    );
    
    // Create confirmation message
    const getConfirmationMessage = () => {
        if (!formData.first_name || !formData.last_name) return "";
        if (formData.has_access && !formData.email) return "";
        
        return t('pro_team_member.add_member.confirm_create_employee', { firstName: formData.first_name, lastName: formData.last_name });
    };
    
    return (
        <>
            <MuiDialog
                open={open}
                onExternalClose={onClose}
                icon={activeStep === 0 ? <GroupsIcon /> : <CalendarMonthIcon />}
                title={activeStep === 0 ? t('pro_team_member.add_member.title') : t('pro_team_member.add_member.step_create_planning')}
                subtitle={activeStep === 0 ? t('pro_team_member.add_member.subtitle') : t('pro_team_member.add_member.step_create_planning')}
                content={dialogContent}
                existAction={true}
                actionTitle={activeStep === 0 ? t('pro_team_member.add_member.create_member_btn') : t('pro_team_member.add_member.create_planning_btn')}
                actionFunction={activeStep === 0 ? handleSubmit : () => {
                    // When user clicks "Créer le planning" in step 2, open the schedule dialog
                    setShowScheduleDialog(true);
                }}
                secondaryAction={activeStep === 1}
                secondaryActionTitle={t('pro_team_member.add_member.skip_planning')}
                secondaryActionFunction={() => {
                    // Skip schedule creation and finish
                    if (onSuccess && createdMember) {
                        onSuccess(t('pro_team_member.add_member.member_created_success'), createdMember);
                    }
                    
                    // Reset form and close main dialog
                    setFormData({
                        email: '',
                        first_name: '',
                        last_name: '',
                        phone: '',
                        service_ids: [],
                        team_ids: [],
                        salon_id: currentSalonId || null
                    });
                    setActiveStep(0);
                    setCreatedMember(null);
                    onClose();
                }}
                isLoading={isCreating}
                maxWidth={activeStep === 0 ? "sm" : "md"}
                confirmationMessage={activeStep === 0 ? getConfirmationMessage() : t('pro_team_member.add_member.confirm_create_planning')}
                showConfirmation={true}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
            {/* AddSchedule Component Dialog */}
            {showScheduleDialog && createdMember && (
                <AddSchedule
                    open={showScheduleDialog}
                    onClose={handleScheduleClose}
                    onSuccess={handleScheduleSuccess}
                    selectedSalonId={activeSalonId}
                    selectedTeamMemberId={createdMember.team_member_id || createdMember.id}
                />
            )}
        </>
    );
};

export default AddMember;
